import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useServiceError } from '@/hooks';
import {
  UpdateWorkOrderComplementRequest,
  WorkOrderComplementItemsHitoryRequest,
  WorkOrderComplementListStatusActionRequest,
} from './WorkOrdersComplements.types';
import {
  updateWorkOrderComplement,
  getWorkOrderComplement,
  authorizeWorkOrderComplement,
  createWorkOrderComplement,
  cancelWorkOrderComplement,
  closeWorkOrderComplement,
  reopenWorkOrderComplement,
  closeVerifyWorkOrderComplement,
  updateItemsWorkOrderComplement,
  updateEstimatedDateWorkOrderComplement,
  workorderComplementInvoiceValues,
  workorderComplementIncludeTag,
  workorderComplementChangeStatus,
  workorderComplementSignaturePublic,
  workorderComplementGenerateActivity,
  workOrderComplementItemsHistory,
  closeAllWorkOrderComplement,
  closeAllVerifyWorkOrderComplement,
  workorderComplementSyncWithPecaAi,
  workorderComplementSyncWithWom,
  workorderComplementBindItemsWithOperational,
  workorderComplementCreatePecaAiOrder,
  workorderComplementBindWithPecaAi,
  workOrderComplementCreatePecaAiOrder,
  workOrderComplementSyncWithMyBCS,
  workOrderComplementEditSchedule,
  verifyAuthorizeWorkOrderComplement,
  workOrderComplementListStatusAction,
} from './WorkOrdersComplements.service';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { workorderDicionaryError } from '..';
import { AxiosError } from 'axios';
import { ErrorFieldErrors } from '@/core/request/ErrorResponse';
import { WORKORDER_ITEMS_QUERY_KEY } from '../WorkOrdersItems/WorkOrdersItems.hooks';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { queryClient } from '@/Root';
import notificationFieldErrorWorkOrder from '@/utils/functions/showErrorNotification';
import { PORTO_POLICIES_BENEFITS_LIST_KEY } from '../../porto/policies/porto-policies.hooks';
import { WORKORDER_PECA_AI_BASKET_DETAIL } from '../pecaAi/workorder-pecaAi.hooks';

export const WORKORDER_COMPLEMENT_DETAIL_KEY = 'workOrders/complements';
export const WORKORDER_COMPLEMENT_ITEM_HISTORY_KEY =
  'workOrders/complements/items-history';
export const WORKORDER_COMPLEMENT_LIST_STATUS_ACTION_KEY =
  'workOrders/complements/list-status-action';

export function useWorkOrderComplement(complementId?: number, enabled = true) {
  const cache = queryClient.getQueryData([
    WORKORDER_COMPLEMENT_DETAIL_KEY,
    complementId,
  ]);

  const query = useQuery(
    [WORKORDER_COMPLEMENT_DETAIL_KEY, complementId],
    () => getWorkOrderComplement(complementId),
    { enabled: !!complementId && !cache && enabled },
  );

  useServiceError(query.error);

  return query;
}

export function useWorkOrderComplementDetail() {
  const query = useMutation(getWorkOrderComplement, {
    onError(err) {
      notificationError(err);
    },
  });

  useServiceError(query.error);

  return query;
}

export function useCreateWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(createWorkOrderComplement, {
    onSuccess(_, vars) {
      notification.success({ message: 'Complemento criado com sucesso.' });

      queryChanel({
        invalidateQueries: [['workOrders', vars.workOrderId]],
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, vars.complementId],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement', vars.complementId],
        ],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useUpdateWorkOrderComplement() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: UpdateWorkOrderComplementRequest) => updateWorkOrderComplement(obj),
    {
      onSuccess(_, vars) {
        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.details.update.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'workOrders.details.update.notifications.success.description',
          }),
        });

        queryChanel({
          resetQueries: [
            ['workOrders'],
            ['workOrders/list'],
            [WORKORDER_COMPLEMENT_DETAIL_KEY],
            ['workOrders/tracking/calendar'],
            ['workOrders/tracking/calendar/complement', vars.complementId],
          ],
        });
      },

      onError(error) {
        notificationFieldErrorWorkOrder({
          error,
          messageDefault: () => notificationError(error),
        });
      },
    },
  );

  return mutation;
}

export function useAuthorizeWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(authorizeWorkOrderComplement, {
    onSuccess(_, vars) {
      notification.success({
        message: 'O orçamento foi autorizado com sucesso.',
      });

      const complementId = vars.complementId;

      queryChanel({
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, complementId],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement', complementId],
          [WORKORDER_ITEMS_QUERY_KEY, { complementId, showRemoved: false }],
        ],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useVerifyAuthorizeWorkOrderComplement() {
  return useMutation(verifyAuthorizeWorkOrderComplement, {
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useCancelWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(cancelWorkOrderComplement, {
    onSuccess(_, vars) {
      notification.success({
        message: 'O orçamento foi cancelado com sucesso.',
      });

      const complementId = vars.complementId;

      queryChanel({
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          ['kanban-schedules'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, complementId],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement', complementId],
          [WORKORDER_ITEMS_QUERY_KEY, { complementId, showRemoved: false }],
        ],
      });
    },
    onError(error) {
      const { response } = error as AxiosError<ErrorFieldErrors>;

      if (
        response?.status === 400 &&
        (response?.data.fieldErrors.financial ||
          response.data.fieldErrors.purchase)
      )
        return;

      notificationError(error, {
        description: 'Não foi possível cancelar o orçamento.',
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useCloseWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(closeWorkOrderComplement, {
    onSuccess(_, vars) {
      notification.success({
        message: 'O orçamento foi fechado com sucesso.',
      });

      queryChanel({
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, vars.complementId],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement', vars.complementId],
          [PORTO_POLICIES_BENEFITS_LIST_KEY],
        ],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useCloseAllWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: closeAllWorkOrderComplement,
    onSuccess() {
      notification.success({
        message: 'Complementos fechados com sucesso',
      });

      queryChanel({
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          ['workOrders/tracking/calendar'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement'],
          [PORTO_POLICIES_BENEFITS_LIST_KEY],
        ],
      });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function useReopenWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(reopenWorkOrderComplement, {
    onSuccess(_, vars) {
      notification.success({
        message: 'O orçamento foi reaberto com sucesso.',
      });

      queryChanel({
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, vars.complementId],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement', vars.complementId],
        ],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useCloseVerifyWorkOrderComplement() {
  return useMutation(closeVerifyWorkOrderComplement, {
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useCloseAllVerifyWorkOrderComplement() {
  return useMutation({
    mutationFn: closeAllVerifyWorkOrderComplement,

    onError(err) {
      notificationError(err);
    },
  });
}

export function useUpdateItemsWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(updateItemsWorkOrderComplement, {
    onSuccess(_, vars) {
      notification.success({
        message: 'Itens atualizados com sucesso',
      });

      const complementId = vars.complementId;

      queryChanel({
        resetQueries: [
          ['dashboard-data'],
          ['workOrders'],
          ['workOrders/list'],
          ['workOrders/tracking/calendar'],
          ['workOrders/tracking/calendar/complement', complementId],
          [WORKORDER_ITEMS_QUERY_KEY, { complementId, showRemoved: false }],
        ],
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useUpdateEstimatedDateWorkOrderComplement() {
  const queryChanel = useQueryChanel();

  return useMutation(updateEstimatedDateWorkOrderComplement, {
    onSuccess(_, data) {
      notification.success({
        message: 'Saída prevista atualizada com sucesso',
      });

      queryChanel({
        resetQueries: [
          ['workOrders'],
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, data.complementId],
        ],
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useWorkOrderComplementInvoiceValues() {
  return useMutation(workorderComplementInvoiceValues, {
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => workorderDicionaryError[err] || err),
      });
    },
  });
}

export function useWorkOrderComplementIncludeTag() {
  const queryChanel = useQueryChanel();

  return useMutation(workorderComplementIncludeTag, {
    onSuccess(_, data) {
      notification.success({
        message: `Etiqueta ${
          !!data.tagId ? 'vinculada' : 'desvinculada'
        } do complemento`,
      });

      queryChanel({
        resetQueries: [
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, data.complementId],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementChangeStatus() {
  const queryChanel = useQueryChanel();

  return useMutation(workorderComplementChangeStatus, {
    onSuccess(_, data) {
      notification.success({
        message: `Status ${
          !!data.statusId ? 'vinculada' : 'desvinculada'
        } no complemento`,
      });

      queryChanel({
        resetQueries: [
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, data.complementId],
          ['kanban-schedules'],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementSignaturePublic() {
  const queryChanel = useQueryChanel();

  return useMutation(workorderComplementSignaturePublic, {
    onSuccess(_, data) {
      notification.success({
        message: 'Orçamento assinado com sucesso',
      });

      queryChanel({
        resetQueries: [['detail-workOrder-qrCode', data.complementGuid]],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('workorder.errors.workorder_auth_item_empty')
              ? 'Nenhum item foi adicionado nesta ordem de serviço.'
              : err,
          ),
      });
    },
  });
}

export function useWorkOrderComplementGenerateActivity() {
  const queryChanel = useQueryChanel();

  return useMutation(workorderComplementGenerateActivity, {
    onSuccess(_, vars) {
      notification.success({
        message: 'Atividade criada com sucesso',
      });

      queryChanel({
        resetQueries: [
          ['workOrders/list'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY, vars.complementId],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementItemsHistory(
  params: WorkOrderComplementItemsHitoryRequest,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [WORKORDER_COMPLEMENT_ITEM_HISTORY_KEY, { ...params }],
    queryFn: ({ pageParam }) =>
      workOrderComplementItemsHistory({
        rows: 10,
        ...params,
        nextToken: pageParam,
      }),
    enabled: !!params.complementId && !!params.workOrderId && enabled,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkOrderComplementBindWithPecaAi() {
  return useMutation({
    mutationFn: workorderComplementBindWithPecaAi,
    onSuccess() {
      notification.success({ message: 'Cesta criada com sucesso' });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkorderComplementSyncWithPecaAi() {
  return useMutation({
    mutationFn: workorderComplementSyncWithPecaAi,
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkorderComplementSyncWithWom() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workorderComplementSyncWithWom,
    onSuccess() {
      queryChanel({ invalidateQueries: [[WORKORDER_ITEMS_QUERY_KEY]] });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkorderComplementBindItemsWithOperational() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workorderComplementBindItemsWithOperational,
    onSuccess() {
      queryChanel({ invalidateQueries: [[WORKORDER_ITEMS_QUERY_KEY]] });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkorderComplementCreatePecaAiOrder() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workorderComplementCreatePecaAiOrder,
    onSuccess(_, vars) {
      queryChanel({
        refetchQueries: [[WORKORDER_COMPLEMENT_DETAIL_KEY, vars.complementId]],
        invalidateQueries: [[WORKORDER_ITEMS_QUERY_KEY]],
      });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkOrderComplementCreatePecaAiOrder() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workOrderComplementCreatePecaAiOrder,
    onSuccess() {
      notification.success({ message: 'Pedido de compra gerado com sucesso' });

      queryChanel({ invalidateQueries: [[WORKORDER_PECA_AI_BASKET_DETAIL]] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementSyncWithMyBCS() {
  return useMutation(workOrderComplementSyncWithMyBCS, {
    onSuccess() {
      message.success('Dados sincronizados com o MyBCS com sucesso.', 5);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementEditSchedule() {
  const queryChanel = useQueryChanel();

  return useMutation(workOrderComplementEditSchedule, {
    onSuccess(_, vars) {
      notification.success({
        message: 'Dados do agendamento atualizados com sucesso.',
      });
      queryChanel({
        invalidateQueries: [
          [WORKORDER_COMPLEMENT_DETAIL_KEY, vars.complementId],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementListStatusAction(
  params: WorkOrderComplementListStatusActionRequest,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [WORKORDER_COMPLEMENT_LIST_STATUS_ACTION_KEY, { ...params }],
    queryFn: ({ pageParam }) =>
      workOrderComplementListStatusAction({
        rows: 10,
        order: 'DESC',
        ...params,
        nextToken: pageParam,
      }),
    enabled: !!params.complementId && !!params.workOrderId && enabled,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    onError(err) {
      notificationError(err);
    },
  });
}
